import { Calendar, Check, ExternalLink, Leaf } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const BOOKING_URL = "https://home.shortcutssoftware.com/ah#/_h/sites/6509/details";

const productRanges = [
  {
    name: 'Haircare',
    description: 'From colour-treated to fine, dry, or damaged hair — La Biosthetique\'s haircare portfolio offers targeted solutions that genuinely transform hair health. Shampoos, conditioners, masks, and treatments formulated with minimum 90% natural ingredients.',
    highlights: ['Power Repair for damaged hair', 'Long Hair protective range', 'Volume boosting for fine hair', 'Hydrating treatments for dry hair'],
  },
  {
    name: 'Professional Colour',
    description: 'Our colour system from La Biosthetique Paris delivers rich, long-lasting results with exceptional grey coverage and vibrancy. The colour formulations protect hair integrity while delivering stunning, multi-dimensional results.',
    highlights: ['Rich, long-lasting colour', 'Excellent grey coverage', 'Gentle on hair structure', 'Multi-dimensional tones'],
  },
  {
    name: 'Scalp Care',
    description: 'Born from their origins in scalp care since 1942, La Biosthetique offers one of the largest portfolios of scalp treatments among beauty brands. From hair loss prevention to sensitive scalp solutions, every product is dermatologically tested.',
    highlights: ['Hair loss control treatments', 'Sensitive scalp solutions', 'Hydration for dry, flaky scalp', 'Oil-balancing for oily roots'],
  },
];

const brandValues = [
  { title: 'Born in Paris', description: 'The ultimate blend of understated luxury and French savoir-faire.' },
  { title: 'Made in Germany', description: 'Manufactured in the pristine foothills of the Black Forest with pure mineral-rich waters.' },
  { title: '90% Natural Ingredients', description: 'Sustainably sourced raw ingredients from around the globe — only the purest, highest grade.' },
  { title: 'No Animal Testing', description: 'Cruelty-free since inception, with a firm stance against animal testing.' },
  { title: 'Dermatologically Tested', description: 'Clinical testing through leading European universities for proven efficacy and safety.' },
  { title: '100% Carbon Neutral', description: 'La Biosthetique is committed to offsetting their entire carbon footprint.' },
];

export default function OurProducts() {
  return (
    <main className="min-h-screen bg-[#FFFDF9]" data-testid="our-products-page">
      <SEO page="ourProducts" />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_1dc100c1-fd6e-4e96-8e6a-366ccfb0323b/artifacts/2fmir4x4_blonde%20lab.png"
            alt="La Biosthetique blonde hair care"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFFDF9] via-[#FFFDF9]/95 to-[#FFFDF9]/70" />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#8D7B68]/10 border border-[#8D7B68]/30 rounded-full px-4 py-2 mb-6">
              <Leaf className="w-4 h-4 text-[#8D7B68]" />
              <span className="text-sm text-[#5D4A3A] font-medium">Our Hair Products</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#2C2420] leading-[1.1] mb-6">
              La Biosthetique Paris
            </h1>
            <p className="text-xl text-[#8D7B68] italic mb-4">
              "Born in Paris. Made in Germany."
            </p>
            <p className="text-lg text-[#5D4A3A] leading-relaxed mb-8">
              At Artistry House, we exclusively use La Biosthetique for all our hair colour, haircare, and scalp treatments. To be a brand born in Paris with products manufactured in Germany is the ultimate blend of understated luxury and quality-obsession. Every drop of every product delivers real, transformative results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-8 py-4 text-lg" data-testid="labio-book-btn">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Hair Appointment
                </Button>
              </a>
              <a href="https://labiosthetique.com.au/" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-[#3D352D] text-[#3D352D] hover:bg-[#3D352D] hover:text-white rounded-full px-8 py-4 text-lg">
                  <ExternalLink className="w-5 h-5 mr-2" />
                  Visit La Biosthetique
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Values */}
      <section className="py-16 bg-[#2C2420]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center text-white">
            {brandValues.map((value) => (
              <div key={value.title}>
                <p className="text-lg font-medium mb-1">{value.title}</p>
                <p className="text-xs text-[#BCAAA4]">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Ranges */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">What We Use</p>
            <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">Our La Biosthetique Ranges</h2>
            <p className="text-lg text-[#5D4A3A] max-w-2xl mx-auto">
              We use their haircare, professional colour, and scalp care ranges — carefully selected to deliver the best results for every client.
            </p>
          </div>

          <div className="space-y-8">
            {productRanges.map((range) => (
              <div key={range.name} className="bg-white p-8 md:p-10 rounded-lg border border-[#D4C4B0]/50 hover:border-[#8D7B68]/50 transition-colors">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <h3 className="text-2xl font-medium text-[#2C2420] mb-4">{range.name}</h3>
                    <p className="text-[#5D4A3A] leading-relaxed">{range.description}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#8D7B68] mb-3">Highlights</p>
                    <div className="space-y-2">
                      {range.highlights.map((h) => (
                        <div key={h} className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-[#8D7B68] flex-shrink-0" />
                          <span className="text-sm text-[#2C2420]">{h}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* R&D Section */}
      <section className="py-24 md:py-32 bg-[#E8DED2]/30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">Science-Backed Beauty</p>
              <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
                Research & Development
              </h2>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-6">
                La Biosthetique's formulations, testing, and manufacturing take place in the pristine foothills of the Black Forest in Germany. Their development team of chemists, biochemists, and dermatologists create scientifically backed products with 75 years of experience.
              </p>
              <p className="text-lg text-[#5D4A3A] leading-relaxed mb-6">
                In addition to using pure, mineral-rich fresh waters from the Black Forest, every product contains a minimum of 90% natural ingredients. Raw ingredients are sustainably sourced from small clay quarries in Switzerland to argan farmers in North Africa.
              </p>
              <p className="text-lg text-[#5D4A3A] leading-relaxed">
                From their very beginning in scalp care circa 1942, dermatological tolerance is strictly adhered to. Their first product — a soothing scalp tonic for babies' sensitive scalps — was developed using natural ingredients, and that commitment to gentle, effective care continues today.
              </p>
            </div>
            <div className="aspect-[4/5] rounded-lg overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_1dc100c1-fd6e-4e96-8e6a-366ccfb0323b/artifacts/2fmir4x4_blonde%20lab.png"
                alt="La Biosthetique blonde hair"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sensory */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-sm uppercase tracking-widest text-[#8D7B68] mb-4">The Luxury Experience</p>
          <h2 className="text-4xl md:text-5xl font-medium text-[#2C2420] mb-6">
            A Sensory Approach
          </h2>
          <p className="text-lg text-[#5D4A3A] max-w-3xl mx-auto mb-8">
            La Biosthetique's product ranges contain a library of signature scents created by renowned perfumers across Germany and Paris. Even the surface of their bottles is designed with 'soft touch' plastic, scored microscopically for a velvety feel in hand. With 100% recyclable packaging and no sticker labels, luxury meets sustainability.
          </p>
          <p className="text-lg text-[#5D4A3A] max-w-3xl mx-auto">
            When you visit Artistry House, you'll experience these products firsthand — the premium textures, the beautiful fragrances, and the transformative results that only science-backed, high-grade ingredients can deliver.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#2C2420] text-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-4xl font-medium mb-6">
            Experience La Biosthetique at Artistry House
          </h2>
          <p className="text-lg text-[#BCAAA4] max-w-2xl mx-auto mb-8">
            Book a hair appointment and discover why we trust La Biosthetique for every colour, treatment, and styling service we offer.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#8D7B68] hover:bg-[#6B5D4D] text-white rounded-full px-10 py-6 text-lg" data-testid="labio-book-cta-btn">
              <Calendar className="w-5 h-5 mr-2" />
              Book Hair Appointment
            </Button>
          </a>
        </div>
      </section>
    </main>
  );
}
